/**
 * Bundle 156a - centralized testimonial display formatter.
 *
 * Signature:
 *   formatTestimonialDisplay(t) -> { quote_line, attribution_line }
 *
 * The `attribution_line` composes 4 optional tokens in this
 * exact order:
 *   1. identifier  (first_name OR initials, always present when
 *                    attribution_display is set)
 *   2. age         (skip if missing)
 *   3. gender      (skip if missing; the M/F single letter form)
 *   4. event_title (skip if missing; separated with a middle-dot
 *                    to visually break the identity block from
 *                    the event context)
 *
 * Missing fields collapse cleanly; separators are inserted only
 * between neighboring present fields. NO em / en dashes anywhere
 * per Bundle 156a guardrail.
 *
 * Example outputs:
 *   Sarah, 29, F  ·  Summer of Dosti Dallas
 *   S.M., 34, M
 *   S.M.  ·  Delhi Dawaat
 *   Sarah, 29
 *   Sarah
 */

const _quote = (text) => {
  const s = String(text || '').trim();
  return s ? `"${s}"` : '';
};

export const formatTestimonialDisplay = (t) => {
  const parts = formatTestimonialParts(t);
  const { quote_line, identity, event_title } = parts;
  let attribution_line = '';
  if (identity && event_title) {
    attribution_line = `${identity}  \u00B7  ${event_title}`;
  } else if (identity) {
    attribution_line = identity;
  } else if (event_title) {
    attribution_line = event_title;
  }
  return { quote_line, attribution_line };
};

/**
 * Bundle 156b - split-parts helper. Returns the identity block
 * and event title separately so the public TestimonialCard can
 * soft-link the event title without breaking the middle-dot
 * separator layout. Consumers that only need a plain string
 * should keep calling formatTestimonialDisplay.
 */
export const formatTestimonialParts = (t) => {
  if (!t || typeof t !== 'object') {
    return { quote_line: '', identity: '', event_title: '' };
  }
  const quote_line = _quote(
    t.quote_text_final || t.current_display_text || '');
  const identifier = String(t.attribution_display || '').trim();
  const age = (typeof t.display_age === 'number' && t.display_age > 0)
    ? String(t.display_age) : '';
  const gender = String(t.display_gender || '').trim();
  const eventTitle = String(t.event_title || '').trim();
  const identity = [identifier, age, gender].filter(Boolean).join(', ');
  return { quote_line, identity, event_title: eventTitle };
};
